import { env } from "@frontend/configs/env.config";
import { useSyncExternalStore } from "react";

/**
 * Is the app able to reach the server? `navigator.onLine` only knows about
 * the network interface (a shop Wi-Fi with no internet still reads "online"),
 * so a failed request also marks the app offline. While offline a tiny probe
 * runs every few seconds and flips the state back as soon as the server
 * answers. Online-only app: there is no queue to replay.
 */

const PROBE_URL = `${env.VITE_API_URL || ""}/api/brand/branding.json`;
const PROBE_EVERY_MS = 5_000;
const PROBE_TIMEOUT_MS = 4_000;

let online = typeof navigator === "undefined" ? true : navigator.onLine;
const listeners = new Set<() => void>();
let probeTimer: ReturnType<typeof setInterval> | null = null;

const emit = () => {
	for (const listener of listeners) listener();
};

const setOnline = (next: boolean) => {
	if (next === online) return;
	online = next;
	if (online) stopProbing();
	else startProbing();
	emit();
};

/** One reachability check; true when the server answered at all. */
export async function probeServer(): Promise<boolean> {
	try {
		await fetch(PROBE_URL, {
			method: "GET",
			cache: "no-store",
			credentials: "omit",
			signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
		});
		setOnline(true);
		return true;
	} catch {
		setOnline(false);
		return false;
	}
}

function startProbing() {
	if (probeTimer) return;
	probeTimer = setInterval(() => void probeServer(), PROBE_EVERY_MS);
}

function stopProbing() {
	if (!probeTimer) return;
	clearInterval(probeTimer);
	probeTimer = null;
}

/** A request failed before reaching the server (fetch TypeError): confirm with a probe. */
export function reportPossibleNetworkError(error: unknown): void {
	const message = error instanceof Error ? error.message : String(error);
	if (/failed to fetch|networkerror|load failed|network request failed/i.test(message)) {
		void probeServer();
	}
}

if (typeof window !== "undefined") {
	window.addEventListener("offline", () => setOnline(false));
	// The interface came back: trust it only once the server answers.
	window.addEventListener("online", () => void probeServer());
	if (!online) startProbing();
}

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};

export const useOnlineStatus = (): boolean =>
	useSyncExternalStore(
		subscribe,
		() => online,
		() => true,
	);
